import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle } from "lucide-react";

const statusColors = {
  submitted: "bg-blue-50 text-blue-700",
  under_review: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  completed: "bg-emerald-50 text-emerald-700",
  awaiting_payment: "bg-amber-50 text-amber-700",
};

export default function TenantApplicationPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(roomId || "");
  const [form, setForm] = useState({
    university: "", student_id: "", year_of_study: "", program: "",
    emergency_name: "", emergency_phone: "", emergency_relation: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/applications").then((r) => {
        const active = r.data.find((a) => !["rejected", "cancelled"].includes(a.status));
        setExisting(active || null);
      }),
      api.get("/rooms?status=available").then((r) => setRooms(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoom) { toast.error("Please select a room"); return; }
    setSubmitting(true);
    try {
      await api.post("/applications", {
        room_id: selectedRoom,
        personal_info: {
          university: form.university,
          student_id: form.student_id,
          year_of_study: form.year_of_study,
          program: form.program,
        },
        emergency_contact: {
          name: form.emergency_name,
          phone: form.emergency_phone,
          relation: form.emergency_relation,
        },
      });
      toast.success("Application submitted successfully!");
      navigate("/tenant");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Application">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  if (existing) {
    return (
      <DashboardLayout title="My Application">
        <Card className="max-w-2xl border-border/60" data-testid="existing-application">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading">Application Status</CardTitle>
              <Badge className={statusColors[existing.status]}>{existing.status?.replace(/_/g, " ")}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Room:</span> <span className="font-medium">{existing.room_number}</span></div>
              <div><span className="text-muted-foreground">Category:</span> <span className="font-medium">{existing.room_category}</span></div>
              <div><span className="text-muted-foreground">Submitted:</span> <span className="font-medium">{new Date(existing.created_at).toLocaleDateString()}</span></div>
              <div><span className="text-muted-foreground">Updated:</span> <span className="font-medium">{new Date(existing.updated_at).toLocaleDateString()}</span></div>
            </div>
            {existing.admin_notes && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Admin Notes</p>
                <p className="text-sm">{existing.admin_notes}</p>
              </div>
            )}
            {existing.status === "approved" && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <p className="text-sm text-emerald-700 font-medium">Your application has been approved! Proceed to payments.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="New Application">
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6" data-testid="application-form">
        {/* Room Selection */}
        <Card className="border-border/60">
          <CardHeader><CardTitle className="font-heading text-lg">Select a Room</CardTitle></CardHeader>
          <CardContent>
            <Select value={selectedRoom} onValueChange={setSelectedRoom}>
              <SelectTrigger className="h-12" data-testid="room-select">
                <SelectValue placeholder="Choose a room" />
              </SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r._id} value={r._id}>
                    Room {r.number} — {r.category} — ${r.price}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card className="border-border/60">
          <CardHeader><CardTitle className="font-heading text-lg">Personal Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>University / Institution</Label>
                <Input value={form.university} onChange={update("university")} className="h-11 mt-1.5" required data-testid="input-university" />
              </div>
              <div>
                <Label>Student ID</Label>
                <Input value={form.student_id} onChange={update("student_id")} className="h-11 mt-1.5" required data-testid="input-student-id" />
              </div>
              <div>
                <Label>Year of Study</Label>
                <Input value={form.year_of_study} onChange={update("year_of_study")} className="h-11 mt-1.5" required data-testid="input-year" />
              </div>
              <div>
                <Label>Program / Course</Label>
                <Input value={form.program} onChange={update("program")} className="h-11 mt-1.5" required data-testid="input-program" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="border-border/60">
          <CardHeader><CardTitle className="font-heading text-lg">Emergency Contact</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input value={form.emergency_name} onChange={update("emergency_name")} className="h-11 mt-1.5" required data-testid="input-emergency-name" />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={form.emergency_phone} onChange={update("emergency_phone")} className="h-11 mt-1.5" required data-testid="input-emergency-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label>Relationship</Label>
                <Input value={form.emergency_relation} onChange={update("emergency_relation")} className="h-11 mt-1.5" required data-testid="input-emergency-relation" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" className="w-full h-12 bg-[#E25822] hover:bg-[#E25822]/90 text-white" disabled={submitting} data-testid="submit-application-btn">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Application"}
        </Button>
      </form>
    </DashboardLayout>
  );
}
